import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Form, Button, notification, Modal, Switch, Skeleton } from 'antd';

import { queryClient } from '@/Root';

import { PageProps } from '@/types/PageProps';

import SuppliersGeneralInfoForm from '@/components/Suppliers/SuppliersGeneralInfoForm';
import { Supplier } from '@/core/domain/people/Suppliers/Suppliers.types';
import { applyMask, removeMask } from '@/utils/mask';
import {
  useDeleteSupplier,
  useUpdateSupplier,
} from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { DefaultRouteTabs, SaveButton } from '@/components/_common';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { cpfMask } from '@/utils/mask/commonMasks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useWindowSize } from '@/hooks';
import { useSupplierFormContext } from '@/components/Suppliers/SupplierFormProvider';

import SupplierFooter from '@/components/Suppliers/SupplierFooter';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import supplierValidationTabs from '@/utils/functions/supplierValidationTabs';

function SupplierGeneralPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const { isMobile } = useWindowSize();

  const [form] = Form.useForm<Supplier>();

  const { setParamLabel } = useBreadcrumbs();

  const {
    isLoading,
    forms,
    supplierId,
    disabled,
    handleActive,
    activeSupplier,
    setActiveSupplier,
    loadActive,
    setLoadActive,
    supplier,
    loadingDetail,
  } = useSupplierFormContext();

  const isSupplierGlobal = !supplier?.matrixId;

  const updateMutation = useUpdateSupplier();
  const deleteMutation = useDeleteSupplier();

  useEffect(() => {
    if (forms.supplierGeneral && !!Object.keys(forms.supplierGeneral).length) {
      form.setFieldsValue({
        ...forms.supplierGeneral,
      });
    } else if (supplier) {
      setActiveSupplier(supplier.active);
      setLoadActive(false);
      form.setFieldsValue({
        ...supplier,
        cpf: supplier.cpf ? applyMask(supplier.cpf, cpfMask) : '',
      });

      setParamLabel(':supplierId', supplier.name);
    }
  }, [supplier, forms.supplierGeneral]);

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'suppliers.remove' }),
      content: `${intl.formatMessage({
        id: 'suppliers.remove.modal.content',
      })}`,
      onOk: async () => {
        try {
          if (supplierId) {
            await deleteMutation.mutateAsync(+supplierId);
          }

          history.push('/app/registry/suppliers');
        } catch (err) {
          notification.error({
            message: intl.formatMessage({
              id: 'commons.form.notification.error',
            }),
            description: intl.formatMessage({
              id: 'commons.form.notification.errorDescription',
            }),
          });
        }
      },

      okText: intl.formatMessage({
        id: 'suppliers.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  };

  const handleSaveClick = async () => {
    try {
      const { ...values } = await form.validateFields();

      const obj = {
        ...values,
        cpf: removeMask(values.cpf),
        cnpj: removeMask(values.cnpj),
        id: Number(supplierId),
        active: activeSupplier,
      };

      await updateMutation.mutateAsync(obj);

      await queryClient.refetchQueries(['supplier', supplierId]);
    } catch (err) {
      notification.error({
        message: intl.formatMessage({
          id: 'commons.form.notification.error',
        }),
        description: intl.formatMessage({
          id: 'commons.form.notification.errorDescription',
        }),
      });
    }
  };

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={supplierValidationTabs(route, supplier)}
      baseKey={`${supplierId}`}
      basePath={`/app/registry/suppliers/${supplierId}`}
      extra={
        <SpaceForButton flexDirection={isMobile ? 'column-reverse' : 'row'}>
          <Switch
            disabled={
              isSupplierGlobal ||
              !hasPermissions([Authorities.REGISTER_SUPPLIER_ACTIVE_DEACTIVE])
            }
            unCheckedChildren={intl.formatMessage({
              id: 'commons.entity.inactive',
            })}
            checkedChildren={intl.formatMessage({
              id: 'commons.entity.active',
            })}
            loading={loadActive}
            checked={activeSupplier}
            onChange={handleActive}
          />

          <Button
            block
            danger
            onClick={handleRemoveClick}
            loading={deleteMutation.isLoading}
            disabled={
              isSupplierGlobal ||
              !hasPermissions([Authorities.REGISTER_SUPPLIER_DELETE])
            }
          >
            {intl.formatMessage({ id: 'commons.form.button.remove' })}
          </Button>

          <SaveButton
            block
            onClick={handleSaveClick}
            loading={isLoading}
            disabled={
              isSupplierGlobal ||
              disabled ||
              !hasPermissions([Authorities.REGISTER_SUPPLIER_EDIT])
            }
          >
            {intl.formatMessage({ id: 'commons.form.button.save' })}
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Skeleton loading={loadingDetail}>
        <SuppliersGeneralInfoForm isEdit form={form} />

        <SupplierFooter disabled={disabled} />
      </Skeleton>
    </DefaultRouteTabs>
  );
}

export default SupplierGeneralPage;
